/**
 * Lightweight browser-side audio metadata reader
 * ─────────────────────────────────────────────
 * Parses ID3v2.3 / ID3v2.4 tags from MP3 files and
 * extracts duration from any audio format via AudioContext.
 *
 * No external dependencies — runs entirely in the browser.
 */

export interface AudioMetadata {
  title?: string;
  artist?: string;          // TPE1 — lead performer
  albumArtist?: string;     // TPE2 — band / album artist
  album?: string;           // TALB
  trackNumber?: string;     // TRCK  (e.g. "3" or "3/12")
  year?: string;            // TDRC / TYER
  genre?: string;           // TCON
  composer?: string;        // TCOM — writer / composer
  lyricist?: string;        // TEXT
  publisher?: string;       // TPUB
  isrc?: string;            // TSRC
  copyright?: string;       // TCOP
  bpm?: string;             // TBPM
  artworkDataUrl?: string;  // APIC — embedded album art (data URL)
  artworkMimeType?: string;
  durationSeconds?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readSynchsafeInt(buf: Uint8Array, offset: number): number {
  return (
    ((buf[offset] & 0x7f) << 21) |
    ((buf[offset + 1] & 0x7f) << 14) |
    ((buf[offset + 2] & 0x7f) << 7) |
    (buf[offset + 3] & 0x7f)
  );
}

function readUint32BE(buf: Uint8Array, offset: number): number {
  return (
    (buf[offset] << 24) |
    (buf[offset + 1] << 16) |
    (buf[offset + 2] << 8) |
    buf[offset + 3]
  );
}

function decodeText(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';
  const enc = bytes[0];
  const data = bytes.slice(1);
  try {
    if (enc === 0) return new TextDecoder('windows-1252').decode(data).replace(/\0/g, '').trim();
    if (enc === 1) return new TextDecoder('utf-16').decode(data).replace(/\0/g, '').trim();
    if (enc === 2) return new TextDecoder('utf-16be').decode(data).replace(/\0/g, '').trim();
    return new TextDecoder('utf-8').decode(data).replace(/\0/g, '').trim();
  } catch {
    return new TextDecoder('utf-8', { fatal: false }).decode(data).replace(/\0/g, '').trim();
  }
}

function nullTermEnd(bytes: Uint8Array, enc: number, start: number): number {
  const step = enc === 1 || enc === 2 ? 2 : 1;
  for (let i = start; i < bytes.length - (step - 1); i += step) {
    if (bytes[i] === 0 && (step === 1 || bytes[i + 1] === 0)) return i;
  }
  return bytes.length;
}

// ─── Parse APIC (album art) frame ────────────────────────────────────────────

function parseApic(data: Uint8Array): { mimeType: string; dataUrl: string } | null {
  if (data.length < 4) return null;
  const enc = data[0];
  // Find mime type (null-terminated after enc byte)
  let mimeEnd = 1;
  while (mimeEnd < data.length && data[mimeEnd] !== 0) mimeEnd++;
  const mimeType = new TextDecoder('ascii').decode(data.slice(1, mimeEnd)) || 'image/jpeg';
  // picture type byte
  const picType = data[mimeEnd + 1];
  // description — find its null terminator
  const descStart = mimeEnd + 2;
  const descEnd = nullTermEnd(data, enc, descStart);
  const step = enc === 1 || enc === 2 ? 2 : 1;
  const imgStart = descEnd + step;
  if (imgStart >= data.length) return null;
  const imgData = data.slice(imgStart);
  // Convert to base64 data URL
  let binary = '';
  for (let i = 0; i < imgData.length; i++) binary += String.fromCharCode(imgData[i]);
  const b64 = btoa(binary);
  return { mimeType, dataUrl: `data:${mimeType};base64,${b64}` };
}

// ─── Main ID3v2 parser ────────────────────────────────────────────────────────

function parseId3v2(buf: Uint8Array): Partial<AudioMetadata> {
  const meta: Partial<AudioMetadata> = {};
  if (buf.length < 10) return meta;
  if (buf[0] !== 0x49 || buf[1] !== 0x44 || buf[2] !== 0x33) return meta; // not "ID3"
  const version = buf[3]; // 2, 3, or 4
  const tagSize = readSynchsafeInt(buf, 6);

  let offset = 10;

  // Skip extended header if present (v2.3/v2.4)
  const flags = buf[5];
  if ((flags & 0x40) && (version === 3 || version === 4)) {
    const extSize = version === 4
      ? readSynchsafeInt(buf, 10)
      : readUint32BE(buf, 10);
    offset += extSize;
  }

  const frameIdSize = version === 2 ? 3 : 4;
  const frameSizeLen = version === 2 ? 3 : 4;
  const frameHeaderLen = frameIdSize + frameSizeLen + (version === 2 ? 0 : 2);

  while (offset + frameHeaderLen < 10 + tagSize && offset < buf.length) {
    // Read frame ID
    let frameId = '';
    for (let i = 0; i < frameIdSize; i++) {
      const ch = buf[offset + i];
      if (ch === 0) break;
      frameId += String.fromCharCode(ch);
    }
    if (!frameId || frameId.length < frameIdSize) break;

    // Read frame size
    let frameSize: number;
    if (version === 2) {
      frameSize = (buf[offset + 3] << 16) | (buf[offset + 4] << 8) | buf[offset + 5];
    } else if (version === 4) {
      frameSize = readSynchsafeInt(buf, offset + 4);
    } else {
      frameSize = readUint32BE(buf, offset + 4);
    }

    if (frameSize <= 0 || offset + frameHeaderLen + frameSize > buf.length) break;

    const frameData = buf.slice(offset + frameHeaderLen, offset + frameHeaderLen + frameSize);
    offset += frameHeaderLen + frameSize;

    // Map frame ID to metadata field
    // ID3v2.2 uses 3-char IDs; v2.3/v2.4 use 4-char
    const id = frameId.toUpperCase();
    switch (id) {
      case 'TIT2': case 'TT2': meta.title = decodeText(frameData); break;
      case 'TPE1': case 'TP1': meta.artist = decodeText(frameData); break;
      case 'TPE2': case 'TP2': meta.albumArtist = decodeText(frameData); break;
      case 'TALB': case 'TAL': meta.album = decodeText(frameData); break;
      case 'TRCK': case 'TRK': meta.trackNumber = decodeText(frameData); break;
      case 'TDRC': case 'TYER': case 'TYE': meta.year = decodeText(frameData).slice(0, 4); break;
      case 'TCON': case 'TCO': {
        let genre = decodeText(frameData);
        // ID3v1 genre codes like "(17)" → strip parens
        genre = genre.replace(/^\((\d+)\)(.*)/, (_, code, rest) => rest.trim() || genre);
        meta.genre = genre;
        break;
      }
      case 'TCOM': case 'TCM': meta.composer = decodeText(frameData); break;
      case 'TEXT': case 'TXT': meta.lyricist = decodeText(frameData); break;
      case 'TPUB': case 'TPB': meta.publisher = decodeText(frameData); break;
      case 'TSRC':              meta.isrc = decodeText(frameData); break;
      case 'TCOP': case 'TCR': meta.copyright = decodeText(frameData); break;
      case 'TBPM': case 'TBP': meta.bpm = decodeText(frameData); break;
      case 'APIC': case 'PIC': {
        if (!meta.artworkDataUrl) {
          const apic = parseApic(frameData);
          if (apic) {
            meta.artworkDataUrl = apic.dataUrl;
            meta.artworkMimeType = apic.mimeType;
          }
        }
        break;
      }
      default: break;
    }
  }

  return meta;
}

// ─── Duration via AudioContext ────────────────────────────────────────────────

async function getAudioDuration(file: File): Promise<number | undefined> {
  try {
    // Fast path: use an <audio> element — works for most formats
    const url = URL.createObjectURL(file);
    return await new Promise<number>((resolve) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(isFinite(audio.duration) ? audio.duration : 0);
      };
      audio.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      audio.src = url;
    });
  } catch {
    return undefined;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Read audio metadata from a File.
 * Reads the first 512 KB to capture ID3 tags + parses duration.
 */
export async function readAudioMetadata(file: File): Promise<AudioMetadata> {
  const chunkSize = 512 * 1024; // 512 KB — enough for most ID3 headers + embedded art
  const slice = file.slice(0, chunkSize);
  const arrayBuffer = await slice.arrayBuffer();
  const buf = new Uint8Array(arrayBuffer);

  const id3 = parseId3v2(buf);
  const durationSeconds = await getAudioDuration(file);

  const filenameTitle = stripTrackNumber(file.name.replace(/\.[^.]+$/, ''));
  // Also clean the ID3 title — some CD rippers embed the track number there too
  const id3Title = id3.title ? stripTrackNumber(id3.title) : undefined;

  return {
    ...id3,
    durationSeconds,
    // Prefer cleaned ID3 title; fall back to cleaned filename stem
    title: id3Title || filenameTitle,
  };
}

/**
 * Strip leading track numbers from a song title or filename stem.
 *
 * Handles common CD-ripper patterns:
 *   "01 - Song Name"  → "Song Name"
 *   "01. Song Name"   → "Song Name"
 *   "01 Song Name"    → "Song Name"   (zero-padded number)
 *   "1 - Song Name"   → "Song Name"   (digit + space-dash-space)
 *   "1. Song Name"    → "Song Name"   (digit + period + space)
 *   "01_Song Name"    → "Song Name"
 *
 * Intentionally does NOT strip:
 *   "3 Doors Down", "4 Non Blondes", "2Pac"  (leading digit, no clear separator)
 */
export function stripTrackNumber(raw: string): string {
  return raw
    // Zero-padded: "01 " / "01-" / "01." / "01_" / "01 - "
    .replace(/^0\d+[\s._-]+/, '')
    // Single/multi-digit with space-dash-space: "1 - " / "12 - "
    .replace(/^\d+\s+-\s+/, '')
    // Single/multi-digit with period+space: "1. " / "12. "
    .replace(/^\d+\.\s+/, '')
    .trim();
}

/** Format seconds as mm:ss */
export function formatDuration(seconds: number | undefined): string {
  if (!seconds || !isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
